import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';

/**
 * Optional "extra" breadcrumb segment that pages can push when the user
 * drills into a specific document (e.g. while editing INV-20260507-0055).
 *
 * Usage from a page:
 *   const { setExtra } = useBreadcrumb();
 *   useEffect(() => {
 *     if (editing) setExtra(`#${invoiceNumber}`);
 *     return () => setExtra(null);
 *   }, [editing, invoiceNumber, setExtra]);
 *
 * The shared <Breadcrumbs> component reads this and appends the extra
 * crumb after the URL-derived trail.
 */
const BreadcrumbContext = createContext({
  extra: null,
  setExtra: () => {},
});

export function BreadcrumbProvider({ children }) {
  const [extra, setExtraState] = useState(null);

  // Stable setter so consumers can list it in effect deps without retriggers.
  const setExtra = useCallback((value) => {
    setExtraState(value && String(value).trim() ? String(value) : null);
  }, []);

  const value = useMemo(() => ({ extra, setExtra }), [extra, setExtra]);
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

export function useBreadcrumb() {
  return useContext(BreadcrumbContext);
}
